//Modules
import { getT } from "@hitit/utils";
import { deepClone } from "../../utils/obj";
import moment from "moment"
//Types 
import type {
    GetPlaceDTOResponse,
    StroeAlbumAsset,
    PlaceBuisnessHour,
    StoreItemData,
    CategoryData,
    TranslationDTO,
    PlaceHashTag,
    PostStoreAlbumAsset
} from "@hitit/api/src/types"
import {
    getNationalPhonenumber,
    getInternationalPhonenumber,
} from "../../functions/string/phone";
import type {
    ImageData,
    Category,
    ChangeableDisplayValue,
    Day,
    OpenHour,
    Menu,
    PlaceDTO,
    MixinPlacedata,
    PostMixinPlaceData,
} from "./types"


type BuisnessHoursKey = keyof GetPlaceDTOResponse["placeDTO"]["businessHours"]
type BuisnessHours = GetPlaceDTOResponse["placeDTO"]["businessHours"]


function invariantOf<Type>(value: string): Type {
    return value as unknown as Type
}

export class PlaceGetConverter {

    public static convertChangeableDisplayValues(placeDTO: MixinPlacedata): ChangeableDisplayValue {
        const mainImageAlbumAssets = placeDTO.storeAlbumAssets.find((v) =>
            v.displaySort === -1
        )

        const hashTags =
            placeDTO.hashTags.sort((a, b) => a.displaySort - b.displaySort)
                .map((v) => v.tag);

        const storeDescription = getT(placeDTO.storeNameAndDescription, "description", "ko") || ""

        const callNumber = getNationalPhonenumber(placeDTO.phonenumber, placeDTO.phonenumberCountryCode);

        const albumImages =
            placeDTO.storeAlbumAssets.filter(v => v.displaySort !== -1)
                .sort((a, b) => a.displaySort - b.displaySort)
                .map((v) => this.convertStoreAlbumAssetToPlaceImageStructure(v))

        const breaktime =
            placeDTO.businessHours.description

        const openHours = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"]
            .map((v) => invariantOf<BuisnessHoursKey>(v))
            .map((v) => {
                return {
                    day: v[0].toUpperCase() + v.slice(1, v.length),
                    buisnessHour: (placeDTO.businessHours[v] as PlaceBuisnessHour)
                }
            })
            .map(v => this.convertBuisnessHourToPlaceBuisnessHourStructure(v.buisnessHour, v.day as Day))

        const categories = this.convertHititCategoryToPlaceCategory(placeDTO.categories)
        const youtube = placeDTO.youtube || "";
        const instagram = placeDTO.instagram || "";
        const naverBlog = placeDTO.naverBlog || ""
        return {
            mainImage: this.convertStoreAlbumAssetToPlaceImageStructure(mainImageAlbumAssets),
            hashTags,
            storeDescription,
            callNumber,
            albumImages,
            categories,
            openHours,
            breaktime,
            youtube,
            instagram,
            naverBlog
        }
    }

    public static convertFiexedDisplayValues(placeDTO: PlaceDTO) {
        const storeName = getT(placeDTO.storeNameAndDescription, "name", "ko") || ""
        const addressDTO = placeDTO.addressDTO
        const addressData = {
            position: {
                lat: String(addressDTO.lat),
                lng: String(addressDTO.lng)
            },
            road: addressDTO.formattedAddress1 + " " + addressDTO.detailAddress,
            numb: addressDTO.formattedAddress2 + " " + addressDTO.detailAddress
        }

        const storeCategory =
            placeDTO.storeTypeCodeDTO &&
            getT(placeDTO.storeTypeCodeDTO.translationDTOs, "type2", "ko");

        return {
            storeName,
            addressData,
            storeCategory
        }
    }

    private static convertStoreAlbumAssetToPlaceImageStructure(albumAsset?: StroeAlbumAsset): ImageData {
        return {
            id: albumAsset?.id || "",
            src: albumAsset?.url || null,
            edit: null
        }
    }

    private static convertBuisnessHourToPlaceBuisnessHourStructure(buisnessHour: PlaceBuisnessHour, day: Day): OpenHour {

        const open = buisnessHour.open ? moment(buisnessHour.open, "HH:mm:ss").format("HH:mm") : "";
        const close = buisnessHour.close ? moment(buisnessHour.close, "HH:mm:ss").format("HH:mm") : "";

        return {
            day,
            open,
            close,
            dayOff: buisnessHour.dayOff
        }
    }

    private static convertHititCategoryToPlaceCategory(categories: CategoryData[]): Category[] {
        //Place 에서는 Min을 사용하기 때문에, enabled 카테고리만 보여주어야 한다. 
        return categories
            .filter((c) => c.enabled)
            .sort((a, b) => a.displaySort - b.displaySort)
            .map((v) => {
                const name = v.translationDTOs.find((v) => v.lang == "ko")?.name || "";
                const menus = this.convertHititItemToPlaceItem(v.storeItemDTOs);
                const id = v.id;
                const displaySort = v.displaySort
                return {
                    id,
                    name,
                    menus,
                    displaySort
                }
            })

    }

    private static convertHititItemToPlaceItem(items: StoreItemData[]): Menu[] {
        //Place 정보에서는 Min으로 보이기 때문에, enabled 속성이 존재하는 것만 보여주어야 한다.
        return items
            .filter(i => i.enabled)
            .sort((a, b) => a.displaySort - b.displaySort)
            .map((v) => {
                const translation = v.translationDTOs.find((v) => v.lang == "ko");
                const name = translation?.name || ""
                const desc = translation?.description || ""
                const show = v.placeEnabled;
                const price = v.price
                const uid = v.uid
                const displaySort = v.displaySort;
                return {
                    name,
                    desc,
                    price,
                    show,
                    uid,
                    displaySort
                }
            })
    }
}

export class PlacePostConverter {

    public static convertPlaceDataToPostParam(original: MixinPlacedata, placeData: ChangeableDisplayValue): PostMixinPlaceData {
        const {
            mainImage,
            storeDescription,
            hashTags,
            albumImages,
            callNumber,
            openHours,
            breaktime,
            categories,
            instagram,
            naverBlog,
            youtube
        } = placeData

        const clone = deepClone(original)

        const mainImageStoreAlbumAsset = this.convertImageDataToStoreAlbumAsset(
            mainImage,
            original.storeAlbumAssets.find((v) =>
                v.displaySort === -1 &&
                mainImage.src === v.url &&
                mainImage.id === v.id
            ),
            -1)

        const albumImageStoreAlbumAssets = albumImages.map((v, index) => {
            const origin = original.storeAlbumAssets.find((o) =>
                o.url === v.src &&
                o.id === v.id);
            return this.convertImageDataToStoreAlbumAsset(
                v,
                origin,
                index
            )
        })

        const newStoreAlbumAssets: PostStoreAlbumAsset[] =
            [mainImageStoreAlbumAsset, ...albumImageStoreAlbumAssets]
                .filter((v) => v !== null) as PostStoreAlbumAsset[]
        const newOpenHour: BuisnessHours = openHours.reduce((obj, v) => {
            const propertyname = v.day.toLowerCase();
            obj[propertyname] = this.convertBuisnessHour(v)
            return obj
        }, {} as any)
        newOpenHour.description = this.sanitizeHTML(breaktime)
        newOpenHour.id = ""

        clone.storeNameAndDescription = this.convertStoreDescription(original.storeNameAndDescription, storeDescription)
        clone.hashTags = hashTags
            .filter((v) => v !== "")
            .map((v, index) => this.convertHashtag(v, index));
        clone.phonenumber = getInternationalPhonenumber(callNumber, original.phonenumberCountryCode) || undefined
        clone.phonenumberCountryCode = clone.phonenumber ? original.phonenumberCountryCode || "82" : undefined
        clone.storeAlbumAssets = newStoreAlbumAssets;
        clone.categories = this.updateVisibleInOriginCategory(original.categories, categories)
        clone.businessHours = newOpenHour
        clone.instagram = instagram;
        clone.youtube = youtube;
        clone.naverBlog = naverBlog;
        return clone
    }

    private static convertStoreDescription(storeNameAndDescription: TranslationDTO[], description: string, lang = "ko") {
        const langMatch = storeNameAndDescription.find((d) => d.lang === lang);
        const defaultMatch = storeNameAndDescription.find((d) => d.defaultLang);
        const sanitaized = this.sanitizeHTML(description);

        if (langMatch) {
            langMatch.description = sanitaized === "" ? null : sanitaized;
        } else if (defaultMatch) {
            defaultMatch.description = sanitaized === "" ? null : sanitaized;
        }
        return storeNameAndDescription
    }

    private static sanitizeHTML(html: string): string {

        const emptyString = "<p><br /></p>"

        if (html === emptyString) {
            return ""
        } else {
            return html
        }
    }

    private static convertHashtag(hashTag: string, sortIndex: number, lang = "ko"): PlaceHashTag & { defaultLang: boolean } {
        return {
            tag: hashTag,
            displaySort: sortIndex,
            lang: lang,
            defaultLang: true,
            id: ""
        }
    }

    private static convertBuisnessHour(openhour: OpenHour): PlaceBuisnessHour {
        const { dayOff } = openhour;

        const open = this.convertHHMMtoLocalDate(openhour.open)
        const close = this.convertHHMMtoLocalDate(openhour.close)

        return {
            open,
            close,
            dayOff,
        }
    }

    private static convertHHMMtoLocalDate(HHMM: string): string | null {
        const hhmmRegExp = /^([01][0-9]|2[0-3]):([0-5][0-9])$/;

        if (hhmmRegExp.test(HHMM)) {
            const convert = moment().utcOffset(0);
            convert.set({
                hour: Number(HHMM.split(":")[0]),
                minute: Number(HHMM.split(":")[1]),
            })
            const toDate = convert.format("HH:mm:00")
            return toDate;
        } else {
            return null
        }
    }

    private static convertImageDataToStoreAlbumAsset(imageData: ImageData, origin?: StroeAlbumAsset, displaySort = -0): PostStoreAlbumAsset | null {
        if (!imageData.src) return null;

        if (origin) {
            return {
                id: origin.id,
                storageId: origin.storageId,
                url: imageData.src as string,
                displaySort
            }
        } else {
            return {
                id: null,
                storageId: imageData.src,
                url: null,
                displaySort
            }
        }

    }


    private static updateVisibleInOriginCategory(originals: CategoryData[], placeItems: Category[]) {
        return originals.map((origin) => {
            const findCategory = placeItems.find((v) => origin.id === v.id);
            const clone = deepClone(origin);
            if (findCategory) {
                clone.storeItemDTOs = origin.storeItemDTOs.map((storeItem) => {
                    const findItem = findCategory.menus.find((i) => i.uid === storeItem.uid);
                    let clone = deepClone(storeItem);
                    if (findItem) {
                        // 변경 지점 
                        clone.placeEnabled = findItem.show
                    }
                    return clone;
                })
            }
            return clone
        })
    }
}